import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { APP } from "@/constants/testIds";
import { Loader2, Download, Sparkles } from "lucide-react";

export default function AgentPage({ agent, title, description }) {
  const [extra, setExtra] = useState("");
  const [content, setContent] = useState("");
  const [assetId, setAssetId] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    setContent("");
    try {
      const { data } = await api.post(`/agents/${agent}/generate`, { kind: agent, extra_context: extra });
      setContent(data.content);
      setAssetId(data.asset_id);
      toast.success("Document generated and saved to Assets Library.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const download = async (fmt) => {
    if (!assetId) return;
    try {
      const res = await api.get(`/assets/${assetId}/download`, { params: { fmt }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.replace(/\s/g, "_")}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="label-mono mb-2">AI Module</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">{title}</h1>
        <p className="mt-2 max-w-2xl text-sm text-stone-600">{description}</p>
      </div>

      <div className="card-flat">
        <div className="label-mono mb-2">Additional Context (optional)</div>
        <textarea
          data-testid={APP.agentExtraContext}
          rows={3}
          value={extra}
          onChange={(e) => setExtra(e.target.value)}
          placeholder="Anything you want the AI to focus on — target audience, funding round, market, etc."
          className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
        />
        <div className="mt-4 flex items-center gap-2">
          <button data-testid={APP.agentGenerate} onClick={generate} disabled={loading} className="btn-primary">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</> : <><Sparkles className="h-4 w-4" /> Generate</>}
          </button>
          {assetId && (
            <>
              <button data-testid={APP.assetDownloadDocx} onClick={() => download("docx")} className="btn-secondary">
                <Download className="h-4 w-4" /> Word (.docx)
              </button>
              <button data-testid={APP.assetDownloadPdf} onClick={() => download("pdf")} className="btn-secondary">
                <Download className="h-4 w-4" /> PDF
              </button>
            </>
          )}
        </div>
      </div>

      {content && (
        <div className="card-flat">
          <div className="label-mono mb-3">Generated Document</div>
          <div className="prose prose-sm prose-stone max-w-none whitespace-pre-wrap text-sm leading-relaxed text-slate-900">
            {content}
          </div>
        </div>
      )}
    </div>
  );
}
